/**
 * Mosaic template.
 */
import AssetDescription from '../sections/description/description';
import Content from '../../../shared/types/enums/content';
import Direction from '../../../shared/types/enums/direction';
import FastEvent from '../../../shared/classes/fastEvent';
import Focus from '../../../shared/types/enums/focus';
import MainContainer from '../atoms/container/contentContainer';
import MainContext from '../../../shared/components/utils/context/mainContext';
import MediaProduction from '../../../shared/classes/mediaProduction';
import Mosaic from '../sections/mosaic/mosaic';
import MosaicCard from '../elements/card/mosaicCard';
import MosaicContainer from '../atoms/container/mosaicContainer';
import ScreenUtils from '../../../shared/utils/screen';
import Setting from '../../../shared/classes/setting';
import VerticalAnimation from '../atoms/container/verticalAnimationContainer';
import goToContent from '../../../shared/actions/content/goToContent';
import useBackKeyboard from '../../../shared/components/utils/hooks/useBackKeyboard';
import useMenu from '../../../shared/components/utils/hooks/useMenu';
import useTransform from '../../../shared/components/utils/hooks/useTransform';
import { store } from '../../../shared/store/store';
import { useAppDispatch, useAppSelector } from '../../../shared/store/hooks';
import { useEffect, useRef, useState } from 'react';
import {
  focusSelector,
  setFocus,
} from '../../../shared/store/slices/focus.slice';
import {
  routerSelector,
  setParams,
} from '../../../shared/store/slices/router.slice';

/**
 * Constants
 * - CARD_OFFSET: The offset for the mosaic card.
 */
const CARD_OFFSET = 213;

/**
 * Displays a mosaic of items.
 */
export default function MosaicTemplate({
  animatable = true,
  canOpenFloatingMenu,
  complete,
  description,
  items,
  startsInMenu = false,
}: {
  animatable?: boolean;
  canOpenFloatingMenu: boolean;
  complete: boolean;
  description: boolean;
  items: (FastEvent | MediaProduction | Setting)[];
  startsInMenu?: boolean;
}) {
  // ------------------------------------------------------------
  // Variables
  // ------------------------------------------------------------

  // Dispatch
  const dispatch = useAppDispatch();
  const { focus } = useAppSelector(focusSelector);
  const { params } = useAppSelector(routerSelector);

  const [currentAsset, setCurrentAsset] = useState<
    FastEvent | MediaProduction | undefined
  >(undefined);

  const colsRef = useRef<number>(0);
  const currentAssetRef = useRef<FastEvent | MediaProduction>();
  const indexRef = useRef(0);
  const itemsRef = useRef<(FastEvent | MediaProduction | Setting)[]>([]);
  colsRef.current = Math.min(5, items.length);
  currentAssetRef.current = currentAsset;
  itemsRef.current = items;

  // Style

  const [contentStyle, , , change] = useTransform(
    (params?.current as { verticalIndex?: number })?.verticalIndex || 0,
    animatable ? CARD_OFFSET : 0,
    Direction.VERTICAL
  );

  const verticalStyle = {
    transform:
      focus === Focus.MENU && description
        ? `translateY(-${500 * ScreenUtils.widthRatio}px)`
        : 'translateY(0px)',
  };

  // ------------------------------------------------------------
  // Callbacks
  // ------------------------------------------------------------

  const cardAction = (item: FastEvent | MediaProduction | Setting) =>
    (item as { action: () => void })?.action ||
    (() => complete && goToContent(item as MediaProduction));

  const setStackParams = (verticalIndex?: number, mosaicIndex?: number) => {
    const {
      router,
      focus: { focus },
    } = store.getState();
    const stackParams = {
      ...router.params.current,
    };
    if (mosaicIndex !== undefined) {
      stackParams.index = mosaicIndex;
    }
    if (verticalIndex !== undefined) {
      stackParams.verticalIndex = verticalIndex;
    }
    if (focus !== -1) {
      stackParams.previousFocus = focus;
    }
    dispatch(setParams(stackParams));
  };

  // ------------------------------------------------------------
  // Event handlers
  // ------------------------------------------------------------

  const onChange = (index: number, increment?: number) => {
    if (itemsRef.current && itemsRef.current.length) {
      setCurrentAsset(itemsRef.current[index] as MediaProduction);
    }
    if (increment !== undefined && increment >= 0) {
      change(increment);
    }
    setStackParams(increment, index);
    indexRef.current = index;
  };

  const onMouseEnter = () => {
    dispatch(setFocus(Focus.MOSAIC));
  };

  // ------------------------------------------------------------
  // Menu hook
  // ------------------------------------------------------------

  const outOfBounds = useMenu(Focus.MOSAIC);

  // ------------------------------------------------------------
  // Keyboard hooks
  // ------------------------------------------------------------

  useBackKeyboard();

  // ------------------------------------------------------------
  // Effect hooks
  // ------------------------------------------------------------

  useEffect(() => {
    if (complete) {
      setCurrentAsset(
        items.length &&
          items[0]?.type !== Content.SETTING &&
          (items[0] as MediaProduction)
          ? (items[0] as MediaProduction)
          : undefined
      );
      setStackParams();
    }
  }, [complete]);

  useEffect(() => {
    if (
      complete &&
      focus !== Focus.MENU &&
      focus !== Focus.LOGIN &&
      focus !== Focus.MODAL_BUTTONS &&
      focus !== Focus.MOSAIC &&
      focus !== Focus.FLOATING_MENU
    ) {
      if (params?.current?.index) {
        setCurrentAsset(
          items[
            (params?.current as { index: number })?.index
          ] as MediaProduction
        );
      }
      dispatch(setFocus(Focus.MOSAIC));
    }
  }, [focus, complete, params, items]);

  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------

  return (
    <>
      <MainContext.Provider
        value={{
          asset: currentAsset,
          complete: complete,
          display: {
            background: false,
            description: true,
            empty: false,
            sliders: false,
          },
          setAsset: () => null,
          onChangeElement: () => null,
          outOfBoundsAction: () => null,
        }}
      >
        {description && focus !== Focus.MENU && <AssetDescription />}
        {complete && (
          <MainContainer id={'mosaicContainer'} onMouseEnter={onMouseEnter}>
            <VerticalAnimation
              animation={'main-content-vertical'}
              style={verticalStyle}
              id={'showMosaicCard'}
            >
              <VerticalAnimation
                animation={'mosaic-content-vertical'}
                style={contentStyle}
                id={'showContentMosaicCard'}
              >
                <MosaicContainer
                  displacePosition={description}
                  displaceMenu={startsInMenu}
                  force={true}
                >
                  <Mosaic
                    data={items}
                    floatingMenu={canOpenFloatingMenu}
                    focus={Focus.MOSAIC}
                    maxCols={colsRef.current}
                    outOfBounds={outOfBounds}
                    onChange={onChange}
                  >
                    {items.map((item, index) => {
                      return (
                        <MosaicCard
                          animatable={animatable}
                          index={index}
                          item={item}
                          key={index}
                          maxCols={colsRef.current}
                          action={cardAction(item)}
                          setAsset={(item: MediaProduction) =>
                            setCurrentAsset(item)
                          }
                        />
                      );
                    })}
                  </Mosaic>
                </MosaicContainer>
              </VerticalAnimation>
            </VerticalAnimation>
          </MainContainer>
        )}
      </MainContext.Provider>
    </>
  );
}
